import React, { useState, useEffect, useCallback } from 'react';
import { FaChartBar, FaCalendarAlt, FaUserInjured, FaRupeeSign, FaSpinner, FaSync, FaCheckCircle, FaFileInvoiceDollar } from 'react-icons/fa';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import BottomNav from '../components/BottomNav';
import { getAppointments, getBillings, getDoctorPaymentSummary } from '../services/api';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const buildMonthlyData = (appointments, billings, apptPayments) => {
  const now = new Date();
  const months = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({ month: MONTH_NAMES[d.getMonth()], year: d.getFullYear(), m: d.getMonth(), y: d.getFullYear(), appointments: 0, patients: new Set(), billingRevenue: 0, apptRevenue: 0 });
  }

  appointments.forEach(a => {
    const d = new Date(a.date);
    const entry = months.find(m => m.m === d.getMonth() && m.y === d.getFullYear());
    if (entry) { entry.appointments++; if (a.patientName) entry.patients.add(a.patientName); }
  });

  billings.forEach(b => {
    if (b.status !== 'Paid') return;
    const d = new Date(b.date);
    const entry = months.find(m => m.m === d.getMonth() && m.y === d.getFullYear());
    if (entry) entry.billingRevenue += parseFloat(b.totalAmount || 0);
  });

  apptPayments.forEach(p => {
    const d = new Date(p.date && p.date !== '-' ? p.date + 'T00:00:00' : p.createdAt);
    const entry = months.find(m => m.m === d.getMonth() && m.y === d.getFullYear());
    if (entry) entry.apptRevenue += parseFloat(p.amount || 0);
  });

  return months.map(m => ({ ...m, patients: m.patients.size, totalRevenue: m.billingRevenue + m.apptRevenue }));
};

const countByType = (appointments) => {
  const types = {};
  appointments.forEach(a => { types[a.type] = (types[a.type] || 0) + 1; });
  return types;
};

const TYPE_COLORS = {
  'Consultation': '#E8500A', 'Follow-up': '#FF6B2C', 'Emergency': '#e53935',
  'Telemedicine': '#1976d2', 'Routine Check': '#2e7d32',
};

const fmt = (n) => n >= 1000 ? `₹${(n / 1000).toFixed(1)}K` : `₹${n.toFixed(0)}`;

const Reports = () => {
  const [loading, setLoading]       = useState(true);
  const [monthlyData, setMonthlyData] = useState([]);
  const [typeData, setTypeData]     = useState({});
  const [totals, setTotals]         = useState({ appointments: 0, patients: 0, apptRevenue: 0, billingRevenue: 0 });
  const [apptPayments, setApptPayments] = useState([]);
  const [billings, setBillings]     = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [apptRes, billRes, payRes] = await Promise.allSettled([
        getAppointments(),
        getBillings(),
        getDoctorPaymentSummary(),
      ]);
      const appts   = apptRes.status  === 'fulfilled' ? (apptRes.value.data?.data  || []) : [];
      const bills   = billRes.status  === 'fulfilled' ? (billRes.value.data?.data  || []) : [];
      const payments = payRes.status  === 'fulfilled' ? (payRes.value.data?.data   || []) : [];

      setApptPayments(payments);
      setBillings(bills);
      setMonthlyData(buildMonthlyData(appts, bills, payments));
      setTypeData(countByType(appts));
      setTotals({
        appointments:  appts.length,
        patients:      new Set(appts.map(a => a.patientName)).size,
        apptRevenue:   payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0),
        billingRevenue: bills.filter(b => b.status === 'Paid').reduce((s, b) => s + parseFloat(b.totalAmount || 0), 0),
      });
    } catch {
      setMonthlyData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const maxAppts  = Math.max(...monthlyData.map(d => d.appointments), 1);
  const typeTotal = Object.values(typeData).reduce((s, v) => s + v, 0);
  const totalRevenue = totals.apptRevenue + totals.billingRevenue;

  return (
    <div className="page-wrapper">
      <Header title="Reports" />
      <Sidebar />
      <main className="page-content">
        <div className="page-header">
          <div>
            <h1><FaChartBar style={{ color: 'var(--primary)', marginRight: 8 }} />Reports & Analytics</h1>
            <p>Practice performance overview — last 7 months</p>
          </div>
          <button className="btn-secondary" onClick={load} disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FaSync style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /> Refresh
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-light)' }}>
            <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: 24, marginBottom: 8 }} /><br />Loading reports…
          </div>
        ) : (
          <>
            {/* ── KPI Cards ── */}
            <div className="report-kpis">
              <div className="kpi-card card">
                <div className="kpi-icon" style={{ background: '#e3f2fd' }}><FaCalendarAlt color="#1976d2" /></div>
                <div><div className="kpi-val">{totals.appointments}</div><div className="kpi-label">Total Appointments</div></div>
              </div>
              <div className="kpi-card card">
                <div className="kpi-icon" style={{ background: '#e8f5e9' }}><FaUserInjured color="#2e7d32" /></div>
                <div><div className="kpi-val">{totals.patients}</div><div className="kpi-label">Unique Patients</div></div>
              </div>
              <div className="kpi-card card">
                <div className="kpi-icon" style={{ background: '#fff3e0' }}><FaRupeeSign color="#E8500A" /></div>
                <div><div className="kpi-val">{fmt(totals.apptRevenue)}</div><div className="kpi-label">Appointment Revenue</div></div>
              </div>
              <div className="kpi-card card">
                <div className="kpi-icon" style={{ background: '#f3e5f5' }}><FaFileInvoiceDollar color="#7c3aed" /></div>
                <div><div className="kpi-val">{fmt(totals.billingRevenue)}</div><div className="kpi-label">Billing Revenue</div></div>
              </div>
              <div className="kpi-card card" style={{ gridColumn: 'span 2' }}>
                <div className="kpi-icon" style={{ background: '#e8f5e9' }}><FaCheckCircle color="#2e7d32" /></div>
                <div><div className="kpi-val">{fmt(totalRevenue)}</div><div className="kpi-label">Total Revenue</div></div>
              </div>
            </div>

            {/* ── Monthly Bar Chart ── */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="section-title">Monthly Appointments (Last 7 Months)</div>
              {monthlyData.every(d => d.appointments === 0) ? (
                <div className="empty-state" style={{ padding: '30px 0' }}>
                  <FaChartBar className="empty-state-icon" /><p>No appointment data yet</p>
                </div>
              ) : (
                <div className="bar-chart">
                  {monthlyData.map(d => (
                    <div key={`${d.month}${d.year}`} className="bar-item">
                      <div className="bar-label-top">{d.appointments || ''}</div>
                      <div className="bar-wrap">
                        <div className="bar-fill" style={{ height: `${(d.appointments / maxAppts) * 100}%` }} />
                      </div>
                      <div className="bar-label">{d.month}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Monthly Summary Table ── */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="section-title">Monthly Summary</div>
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr><th>Month</th><th>Appointments</th><th>Patients</th><th>Appt Revenue</th><th>Billing Revenue</th><th>Total</th></tr>
                  </thead>
                  <tbody>
                    {monthlyData.map(d => (
                      <tr key={`${d.month}${d.year}`}>
                        <td><strong>{d.month} {d.year}</strong></td>
                        <td>{d.appointments}</td>
                        <td>{d.patients}</td>
                        <td style={{ color: '#E8500A', fontWeight: 700 }}>₹{d.apptRevenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                        <td style={{ color: '#7c3aed', fontWeight: 700 }}>₹{d.billingRevenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                        <td style={{ color: '#2e7d32', fontWeight: 700 }}>₹{d.totalRevenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Appointment Payments ── */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="section-title">Appointment Payments (Razorpay)</div>
              {apptPayments.length === 0 ? (
                <div className="empty-state" style={{ padding: '24px 0' }}>
                  <FaRupeeSign className="empty-state-icon" /><p>No appointment payments yet</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr><th>Patient</th><th>Date</th><th>Mode</th><th>Amount</th><th>Payment ID</th></tr>
                    </thead>
                    <tbody>
                      {apptPayments.map(p => (
                        <tr key={p.id}>
                          <td><strong>{p.patientName}</strong></td>
                          <td>{p.date !== '-' ? new Date(p.date + 'T00:00:00').toLocaleDateString('en-IN') : new Date(p.createdAt).toLocaleDateString('en-IN')}</td>
                          <td>{p.mode}</td>
                          <td><strong style={{ color: '#2e7d32' }}>₹{parseFloat(p.amount).toLocaleString('en-IN')}</strong></td>
                          <td style={{ fontSize: 11, color: 'var(--text-light)', fontFamily: 'monospace' }}>{p.paymentId || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ── Billing & Payments ── */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="section-title">Billing & Payments</div>
              {billings.length === 0 ? (
                <div className="empty-state" style={{ padding: '24px 0' }}>
                  <FaFileInvoiceDollar className="empty-state-icon" /><p>No billing records yet</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr><th>Patient</th><th>Date</th><th>Amount</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      {billings.map(b => (
                        <tr key={b.id}>
                          <td><strong>{b.patientName || '-'}</strong></td>
                          <td>{b.date ? new Date(b.date).toLocaleDateString('en-IN') : '-'}</td>
                          <td><strong style={{ color: b.status === 'Paid' ? '#2e7d32' : '#e65100' }}>₹{parseFloat(b.totalAmount || 0).toLocaleString('en-IN')}</strong></td>
                          <td><span className={`status-badge status-${b.status?.toLowerCase()}`}>{b.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ── Appointment Types ── */}
            {typeTotal > 0 && (
              <div className="card">
                <div className="section-title">Appointment Types</div>
                <div className="type-breakdown">
                  {Object.entries(typeData).sort((a, b) => b[1] - a[1]).map(([label, count]) => {
                    const pct = Math.round((count / typeTotal) * 100);
                    return (
                      <div key={label} className="type-row">
                        <div className="type-label">{label}</div>
                        <div className="type-bar-wrap">
                          <div className="type-bar" style={{ width: `${pct}%`, background: TYPE_COLORS[label] || '#9e9e9e' }} />
                        </div>
                        <div className="type-pct">{pct}%</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </main>
      <BottomNav />

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .report-kpis { display: grid; grid-template-columns: repeat(2,1fr); gap: 10px; margin-bottom: 16px; }
        .kpi-card { display: flex; align-items: center; gap: 12px; padding: 14px; }
        .kpi-icon { width: 42px; height: 42px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
        .kpi-val { font-size: 18px; font-weight: 800; color: var(--text-dark); }
        .kpi-label { font-size: 11px; color: var(--text-light); font-weight: 600; }
        .bar-chart { display: flex; align-items: flex-end; gap: 8px; height: 160px; padding: 0 4px; }
        .bar-item { flex: 1; display: flex; flex-direction: column; align-items: center; height: 100%; }
        .bar-label-top { font-size: 10px; font-weight: 700; color: var(--text-medium); margin-bottom: 3px; min-height: 14px; }
        .bar-wrap { flex: 1; width: 100%; display: flex; align-items: flex-end; }
        .bar-fill { width: 100%; background: linear-gradient(to top, #E8500A, #FF6B2C); border-radius: 6px 6px 0 0; transition: height 0.5s ease; min-height: 4px; }
        .bar-label { font-size: 11px; color: var(--text-light); font-weight: 600; margin-top: 4px; }
        .type-breakdown { display: flex; flex-direction: column; gap: 12px; }
        .type-row { display: flex; align-items: center; gap: 10px; }
        .type-label { width: 110px; font-size: 13px; font-weight: 600; color: var(--text-medium); flex-shrink: 0; }
        .type-bar-wrap { flex: 1; height: 10px; background: var(--primary-bg); border-radius: 5px; overflow: hidden; }
        .type-bar { height: 100%; border-radius: 5px; transition: width 0.5s ease; }
        .type-pct { width: 36px; font-size: 13px; font-weight: 700; text-align: right; color: var(--text-dark); }
        .btn-secondary { background: var(--primary-bg); border: 1px solid var(--border); color: var(--primary); padding: 8px 14px; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 600; }
        .btn-secondary:hover { background: var(--primary); color: white; }
        .btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }
        @media (min-width: 768px) { .report-kpis { grid-template-columns: repeat(3,1fr); } }
      `}</style>
    </div>
  );
};

export default Reports;
